/* eslint-disable complexity */
/* eslint-disable max-depth */
/* eslint-disable max-lines */
import { gqlClient } from '@redviking/argonaut-core-ui/src/util/gql-client';
import { DeleteMaterialModelDocument, DeleteMaterialModelSupportingConfigDocument, DeleteSerialNumberFormatDocument, DeleteUdfColumnsDocument, GetMaterialClassDocument, InsertMaterialModelDocument, InsertNewSerialNumberFormatDocument, InsertNewSerialNumberFormatMutationVariables, UpdateArgoMediaDocument, UpdateMaterialClassDocument, UpdateMaterialModelDocument, UpdateSerialNumberFormatDocument } from 'types/db';
import { ArgoMaterialClassAttributeSettingInsertInput, ArgoMaterialClassAttributeSettingUpdates } from '@redviking/argonaut-util/types/db-schema/argo/material_class_attribute_setting';
import { ArgoMaterialQuantityKindEnum } from '@redviking/argonaut-util/types/db-enums/argo/material_quantity_kind';
import { ArgoSnFormatStatusEnum } from '@redviking/argonaut-util/types/db-enums/argo/sn_format_status';
import { ArgoUdfColumnConstraint, ArgoUdfColumnUpdateColumn } from '@redviking/argonaut-util/types/db-enums/argo/udf_column';
import { ArgoUdfColumnTypeEnum } from '@redviking/argonaut-util/types/db-enums/argo/udf_column_type';
import { getListChanges } from 'src/util/composables/watch-list-changes';
import cloneDeep from 'lodash.clonedeep';
import { type ExtendedEntityParameters } from 'src/components/EntityDetail';
import type { MaterialClassEntityFragment, ValidationResult } from 'types';
import type { ArgoMaterialModelProcessInsertInput, ArgoMaterialModelProcessUpdates } from '@redviking/argonaut-util/types/db-schema/argo/material_model_process';
import { validateMaterialClass } from './material-class.validations';
import { generateMaterialModelDefaultDataChanges } from 'src/model-number/model-number.entity';
import type { PendingMediaObjectReference } from '@redviking/argonaut-util/src/media.util';
import isEqual from 'lodash.isequal';
import type { SerialNumFmt } from './serial-numbers/serial-number.entity';

export type MaterialClassData = Pick<MaterialClassEntityFragment,
    'id' |
    'name' |
    'meta' |
    'context_id'|
    'created_by' |
    'udf_schemas' |
    'description' |
    'linked_media' |
    'linked_profile_picture' |
    'material_class_processes' |
    'material_class_attribute_settings'
> & {
    created_at?: string;
    serialNumberFormatId: number | null;
    sn_validation_pattern: string;
    material_models: {
        new: boolean;
        modelCfg: MaterialClassEntityFragment['material_models'][number] & {
            serialNumberFormatId: number | null;
        };
    }[];
    material_quantity_type?: ArgoMaterialQuantityKindEnum;
};

export type MaterialClassEntity = {
    ignoreWarnings: boolean;
    materialClass: MaterialClassData;
    pendingClassLinkedMedia: PendingMediaObjectReference[];
    // NOTE: Need to keep this piece of pending media separate from pending media in the store so it can easily be found later
    pendingProfilePictureMedia: PendingMediaObjectReference | null;
    /** keyed by material model id */
    pendingModelLinkedMedia: Record<string, PendingMediaObjectReference[]>;
    serialNumberFormatsRelatedToMaterialConfig: ({
        new: boolean;
    } & SerialNumFmt)[];
};

export const acceptedFileTypes = [ '.pdf', '.png', '.jpeg', '.jpg', '.jpeg' ];

const getMaterialClassAttributeSettingsChanges = (
    materialClassId: string,
    newSettings: MaterialClassEntity['materialClass']['material_class_attribute_settings'],
    oldSettings: MaterialClassEntity['materialClass']['material_class_attribute_settings']
): {
    inserts: ArgoMaterialClassAttributeSettingInsertInput[];
    updates: ArgoMaterialClassAttributeSettingUpdates[];
    deletes: string[];
} => {

    const ret: {
        inserts: ArgoMaterialClassAttributeSettingInsertInput[];
        updates: ArgoMaterialClassAttributeSettingUpdates[];
        deletes: string[];
    } = {
        inserts: [],
        updates: [],
        deletes: oldSettings.filter(oldSetting => !newSettings.find(newSetting => newSetting.id === oldSetting.id)).map(t => t.id),
    };
    
    for (const setting of newSettings) {
        const existingSetting = oldSettings.find(oldSetting => oldSetting.id === setting.id);
        if (existingSetting) {
            ret.updates.push({
            where: {
                id: {
                    _eq: setting.id,
                },
            },
            _set: {
                config: setting.config,
            },
        });
        } else {
            ret.inserts.push({
                id: setting.id,
                config: setting.config,
                material_class_id: materialClassId,
                attribute_schema_id: setting.attribute_schema.id,
            });
        }
    }
    return ret;
};

export async function saveMaterialClassEntity (payload: ExtendedEntityParameters<'materialclass'>): Promise<ValidationResult> {
    const {
        entity,
        oldEntity: originalOldEntity,
    } = payload;

    const result = validateMaterialClass(entity);
    if (result.status !== 'success') {
        return result;
    }

    const udfSchemaIds = entity.materialClass.udf_schemas.map(udfSchema => udfSchema.id);
    const deleteUdfSchemas = originalOldEntity?.materialClass?.udf_schemas.filter(udfSchema => !udfSchemaIds.includes(udfSchema.id)) || [];

    const oldEntity = cloneDeep(originalOldEntity);

    const classProcessChanges = getListChanges(entity.materialClass.material_class_processes, oldEntity?.materialClass?.material_class_processes || [], { partialChanges: false, deep: true, format: 'plc' });

    const attributeMutations = getMaterialClassAttributeSettingsChanges(entity.materialClass.id, entity.materialClass.material_class_attribute_settings, oldEntity?.materialClass.material_class_attribute_settings || []);

    const tempSerialNumberFormatIdToRealIdMap: Record<number, number> = {};

    try {
        // First update existing serial number formats. We need to do this first to set any serial number formats to archived to avoid unique constraint issues with having multiple active serial number formats with the same name
        for (const serialNumberFormat of entity.serialNumberFormatsRelatedToMaterialConfig.filter(snf => !snf.new)) {
            const originalSnf = originalOldEntity?.serialNumberFormatsRelatedToMaterialConfig.find(osnf => osnf.id === serialNumberFormat.id);
            if (!originalSnf) {
                return {
                    status: 'error',
                    routeName: 'materialClassSettings',
                    errorMessage: 'Original serial number format not found. This is incorrect because the serial number format is not new which indicates it should have been feteched on initial load',
                };
            }
            if (!isEqual(originalSnf, serialNumberFormat)) {
                if (serialNumberFormat.status === ArgoSnFormatStatusEnum.Archived) {
                    await gqlClient.request({
                        document: UpdateSerialNumberFormatDocument,
                        variables: {
                            contextId: entity.materialClass.context_id,
                            serialNumberFormatId: serialNumberFormat.id,
                            serialNumberFormat: {
                                status: ArgoSnFormatStatusEnum.Archived,
                            },
                        },
                    });
                } else if (serialNumberFormat.status === ArgoSnFormatStatusEnum.Editing) {
                    await gqlClient.request({
                        document: UpdateSerialNumberFormatDocument,
                        variables: {
                            contextId: entity.materialClass.context_id,
                            serialNumberFormatId: serialNumberFormat.id,
                            serialNumberFormat: {
                                name: serialNumberFormat.name,
                                status: serialNumberFormat.status,
                                config: serialNumberFormat.config,
                                description: serialNumberFormat.description,
                            },
                        },
                    });
                } else if (serialNumberFormat.status === ArgoSnFormatStatusEnum.Activated) {
                    if (originalSnf.status === ArgoSnFormatStatusEnum.Activated) {
                        return {
                            status: 'error',
                            errorMessage: 'Cannot update an activated serial number format. Please create a new one instead.',
                        };
                    } else {
                        await gqlClient.request({
                            document: UpdateSerialNumberFormatDocument,
                            variables: {
                                contextId: entity.materialClass.context_id,
                                serialNumberFormatId: serialNumberFormat.id,
                                serialNumberFormat: {
                                    name: serialNumberFormat.name,
                                    config: serialNumberFormat.config,
                                    status: ArgoSnFormatStatusEnum.Activated,
                                    description: serialNumberFormat.description,
                                },
                            },
                        });
                    }
                } else {
                    const unknownStatus: never = serialNumberFormat.status;
                    return {
                        status: 'error',
                        errorMessage: `Unknown serial number format status: ${unknownStatus}`,
                    };
                }
            }
        }

        // Next we need to insert pending serial number formats so we can get back the id from the database in order to link them properly to their respective class/ model
        for (const serialNumberFormat of entity.serialNumberFormatsRelatedToMaterialConfig.filter(snf => snf.new)) {
            const variables: InsertNewSerialNumberFormatMutationVariables = {
                payload: {
                    name: serialNumberFormat.name,
                    config: serialNumberFormat.config,
                    status: serialNumberFormat.status,
                    material_class_id: entity.materialClass.id,
                    description: serialNumberFormat.description,
                },
            };
            const {
                insert_argo_sn_format_one,
            } = await gqlClient.request({
                variables,
                document: InsertNewSerialNumberFormatDocument,
            });
            if (!insert_argo_sn_format_one) {
                return {
                    status: 'error',
                    errorMessage: 'Failed to insert serial number format',
                };
            }
            tempSerialNumberFormatIdToRealIdMap[serialNumberFormat.id] = insert_argo_sn_format_one.id;
        }

        let materialClassSerialNumberFormatId = entity.materialClass.serialNumberFormatId;
        // if the serial number format id linked to a material class is less than 0 then the material class wants to use a serial number format that has yet to be saved.
        // But at this point it is saved so find it in the mapping created above. The ACTUAL serial number format id SHOULD ALWAYS BE PRESENT at this point so throw an error if not found
        if (materialClassSerialNumberFormatId !== null && materialClassSerialNumberFormatId < 0) {
            materialClassSerialNumberFormatId = tempSerialNumberFormatIdToRealIdMap[materialClassSerialNumberFormatId];
            if (!materialClassSerialNumberFormatId) {
                return {
                    status: 'error',
                    errorMessage: 'Failed to map material class serial number format ID',
                };
            }
        }
        /**
         * NOTE: it is important this query runs first since it handles inserting into the udf column table which is used in a foreign key constraint in the material model default udf table
         * FUTURE: Make a dedicated mutation to insert udf columns since there is no udf column selector the class will always be making new ones
         */
        await gqlClient.request({
            document: UpdateMaterialClassDocument,
            variables: {
                materialClassId: entity.materialClass.id,
                deleteUdfSchemas: deleteUdfSchemas.map(udfSchema => udfSchema.id),
                materialClass: {
                    meta: entity.materialClass.meta,
                    name: entity.materialClass.name,
                    description: entity.materialClass.description,
                    linked_media: entity.materialClass.linked_media,
                    sn_format_id: materialClassSerialNumberFormatId,
                    sn_validation_pattern: entity.materialClass.sn_validation_pattern,
                    linked_profile_picture: entity.materialClass.linked_profile_picture,
                    material_quantity_type: entity.materialClass.material_quantity_type || ArgoMaterialQuantityKindEnum.Distinct,
                },
                udfSchema: entity.materialClass.udf_schemas.map(mudf => ({
                    material_class_id: entity.materialClass.id,
                    udf_column: {
                        on_conflict: {
                            constraint: ArgoUdfColumnConstraint.UdfColumnPkey,
                            update_columns: [
                                ArgoUdfColumnUpdateColumn.Id,
                                ArgoUdfColumnUpdateColumn.DataType,
                                ArgoUdfColumnUpdateColumn.Name,
                                ArgoUdfColumnUpdateColumn.NameUnique,
                                ArgoUdfColumnUpdateColumn.Description,
                                ArgoUdfColumnUpdateColumn.ValidationMax,
                                ArgoUdfColumnUpdateColumn.ValidationMin,
                                ArgoUdfColumnUpdateColumn.ValidationPattern,
                                ArgoUdfColumnUpdateColumn.ValidationRequired,
                                ArgoUdfColumnUpdateColumn.ValidationUnique,
                                ArgoUdfColumnUpdateColumn.Enabled,
                                ArgoUdfColumnUpdateColumn.Type,
                            ],
                        },
                        data: {
                            id: mudf.udf_column.id,
                            name: mudf.udf_column.name,
                            type: ArgoUdfColumnTypeEnum.Material,
                            data_type: mudf.udf_column.data_type,
                            description: mudf.udf_column.description,
                            validation_max: mudf.udf_column.validation_max,
                            validation_min: mudf.udf_column.validation_min,
                            validation_unique: mudf.udf_column.validation_unique,
                            validation_pattern: mudf.udf_column.validation_pattern,
                            validation_required: mudf.udf_column.validation_required,
                        },
                    },
                })),
                deleteMatClassProcessInputs: classProcessChanges.deletes.map(e => e.id),
                materialClassProcesses: classProcessChanges.inserts.map(e => ({
                    id: e.id,
                    process_id: e.process_id,
                    material_class_id: entity.materialClass.id,
                })),
                addMaterialClassAttributeSettings: attributeMutations.inserts,
                updateMaterialClassAttributeSettings: attributeMutations.updates,
                deleteMaterialClassAttributeSettings: attributeMutations.deletes,
            },
        });

        for (const materialModel of entity.materialClass.material_models) {

            let materialModelSerialNumberFormatId = materialModel.modelCfg.serialNumberFormatId;
            // if the serial number format id linked to a material model is less than 0 then the material model wants to use a serial number format that has yet to be saved.
            // But at this point it is saved so find it in the mapping created above. The ACTUAL serial number format id SHOULD ALWAYS BE PRESENT at this point so throw an error if not found
            if (materialModelSerialNumberFormatId !== null && materialModelSerialNumberFormatId < 0) {
                materialModelSerialNumberFormatId = tempSerialNumberFormatIdToRealIdMap[materialModelSerialNumberFormatId];
                if (!materialModelSerialNumberFormatId) {
                    return {
                        status: 'error',
                        errorMessage: 'Failed to map model number serial number format ID',
                    };
                }
            }

            if (materialModel.new) {
                // init query to insert the material model. The array of default udf data and process data will be inserted in the next query
                await gqlClient.request({
                    document: InsertMaterialModelDocument,
                    variables: {
                        model: {
                            id: materialModel.modelCfg.id,
                            meta: materialModel.modelCfg.meta,
                            material_class_id: entity.materialClass.id,
                            description: materialModel.modelCfg.description,
                            sn_format_id: materialModelSerialNumberFormatId,
                            sn_validation_pattern: materialModel.modelCfg.sn_validation_pattern,
                            linked_media: materialModel.modelCfg.linked_media,
                            model_number: materialModel.modelCfg.model_number,
                        },
                    },
                });
            }
            const originalMaterialModel = originalOldEntity?.materialClass.material_models.find(omm => omm.modelCfg.id === materialModel.modelCfg.id);
            if (!originalMaterialModel && !materialModel.new) {
                return {
                    status: 'error',
                    routeName: 'materialClassSettings',
                    errorMessage: 'Original model number not found. This is incorrect because the model number is not new which indicates it should have been feteched on initial load',
                };
            }
            const {
                inserts: materialModelDefaultUdfDataInserts,
                updates: materialModelDefaultUdfDataUpdates,
                deletes: materialModelDefaultUdfDataDeletes,
            } = generateMaterialModelDefaultDataChanges({
                newMaterialModel: materialModel.modelCfg,
                originalMaterialModel: originalMaterialModel?.modelCfg,
            });

            const materialModelProcessUpdates: ArgoMaterialModelProcessUpdates[] = [];
            const materialModelProcessInserts: ArgoMaterialModelProcessInsertInput[] = [];
            const materialModelProcessDeletes = (originalMaterialModel?.modelCfg.material_model_processes || []).filter(omp => {
                const newProcess = materialModel.modelCfg.material_model_processes.find(mmp => mmp.process_id === omp.process_id);
                return !newProcess;
            }).map(omp => omp.id);
            for (const mmp of materialModel.modelCfg.material_model_processes) {
                const originalProcess = originalMaterialModel?.modelCfg.material_model_processes.find(omp => omp.id === mmp.id);
                originalProcess
                    ? materialModelProcessUpdates.push({
                        where: {
                            id: {
                                _eq: originalProcess.id,
                            },
                        },
                        _set: {
                            active: mmp.active,
                        },
                    })
                    : materialModelProcessInserts.push({
                        id: mmp.id,
                        active: mmp.active,
                        process_id: mmp.process_id,
                        material_model_id: materialModel.modelCfg.id,
                    });
            }
            await gqlClient.request({
                document: UpdateMaterialModelDocument,
                variables: {
                    materialModelProcessUpdates,
                    materialModelProcessDeletes,
                    materialModelProcessInserts,
                    materialModelDefaultUdfDataUpdates,
                    materialModelDefaultUdfDataDeletes,
                    materialModelDefaultUdfDataInserts,
                    materialModelId: materialModel.modelCfg.id,
                    newMaterialModel: {
                        meta: materialModel.modelCfg.meta,
                        sn_format_id: materialModelSerialNumberFormatId,
                        sn_validation_pattern: materialModel.modelCfg.sn_validation_pattern,
                        linked_media: materialModel.modelCfg.linked_media,
                        description: materialModel.modelCfg.description,
                    },
                },
            });
        }

        /**
         * Deleting previously save material models
         * FUTURE: This will error if material has been created under this model. In that case we should set the model to disabled
         */
        for (const originalMaterialModel of originalOldEntity?.materialClass.material_models || []) {
            const newMaterialModel = entity.materialClass.material_models.find(mmm => mmm.modelCfg.id === originalMaterialModel.modelCfg.id);
            if (!newMaterialModel) {
                await gqlClient.request({
                    document: DeleteMaterialModelSupportingConfigDocument,
                    variables: {
                        materialModelId: originalMaterialModel.modelCfg.id,
                    },
                });
                await gqlClient.request({
                    document: DeleteMaterialModelDocument,
                    variables: {
                        materialModelId: originalMaterialModel.modelCfg.id,
                    },
                });
            }
        }

        try {
            if (deleteUdfSchemas.length) {
                await gqlClient.request({
                    document: DeleteUdfColumnsDocument,
                    variables: {
                        udfColumnIds: deleteUdfSchemas.map(udfSchema => udfSchema.udf_column.id),
                    },
                });
            }
        } catch (err) {
            console.warn('Cannot delete UDF columns. It is possible that the column is used elsewhere.');
        }

        // Deleting Serial Number Formats that were removed from the entity
        for (const originalSnf of originalOldEntity?.serialNumberFormatsRelatedToMaterialConfig || []) {
            if (!entity.serialNumberFormatsRelatedToMaterialConfig.find(snf => snf.id === originalSnf.id)) {
                await gqlClient.request({
                    document: DeleteSerialNumberFormatDocument,
                    variables: {
                        serialNumberFormatId: originalSnf.id,
                        contextId: entity.materialClass.context_id,
                    },
                });
            }
        }

        // NOTE: due to the number of different items being updated in the update query it is just easier to re-fetch the material class rather than trying to piece-meal the entity back together
        const {
            materialClass: newMaterialClass,
        } = await gqlClient.request({
            document: GetMaterialClassDocument,
            variables: {
                materialClassId: entity.materialClass.id,
            },
        });
        if (!newMaterialClass) {
            return {
                status: 'error',
                errorMessage: 'Material class update query did not return a new material class',
            };
        }
        const newEntity: MaterialClassEntity = {
            ignoreWarnings: false,
            pendingClassLinkedMedia: [],
            pendingModelLinkedMedia: {},
            pendingProfilePictureMedia: null,
            serialNumberFormatsRelatedToMaterialConfig: newMaterialClass.serial_number_formats.map(snf => ({
                ...snf,
                new: false,
            })),
            materialClass: {
                id: newMaterialClass.id,
                name: newMaterialClass.name,
                meta: newMaterialClass.meta,
                created_at: newMaterialClass.created_at,
                created_by: newMaterialClass.created_by,
                context_id: newMaterialClass.context_id,
                udf_schemas: newMaterialClass.udf_schemas,
                description: newMaterialClass.description,
                linked_media: newMaterialClass.linked_media,
                serialNumberFormatId: newMaterialClass.sn_format?.id || null,
                sn_validation_pattern: newMaterialClass.sn_validation_pattern,
                linked_profile_picture: newMaterialClass.linked_profile_picture,
                material_class_processes: newMaterialClass.material_class_processes,
                material_class_attribute_settings: newMaterialClass.material_class_attribute_settings,
                material_quantity_type: newMaterialClass.material_quantity_type as ArgoMaterialQuantityKindEnum, // this should always be set but default to distinct if not
                material_models: newMaterialClass.material_models.map<MaterialClassEntity['materialClass']['material_models'][number]>(mm => ({
                    new: false,
                    modelCfg: {
                        ...mm,
                        serialNumberFormatId: mm.sn_format?.id || null,
                        sn_validation_pattern: mm.sn_validation_pattern,
                    },
                })),
            },
        };

        // update the meta data for internal media
        for (const linkedMedia of newEntity.materialClass.linked_media) {
            if (linkedMedia.type === 'internal') {
                const existingLinkedInternalMedia = oldEntity?.materialClass.linked_media.find(olm => olm.type === 'internal' && linkedMedia.type === 'internal' && olm.file.id === linkedMedia.file.id);
                // If the media is internal and the meta data has changed, update it
                if (!existingLinkedInternalMedia || (existingLinkedInternalMedia.type === 'internal' && !isEqual(existingLinkedInternalMedia.file.meta, linkedMedia.file.meta))) {
                    await gqlClient.request({
                        document: UpdateArgoMediaDocument,
                        variables: {
                            mediaId: linkedMedia.file.id,
                            mediaInput: {
                                meta: linkedMedia.file.meta,
                            },
                        },
                    });
                }
            }
        }
        for (const model of newEntity.materialClass.material_models) {
            for (const linkedMedia of model.modelCfg.linked_media) {
                if (linkedMedia.type === 'internal') {
                    const existingModel = oldEntity?.materialClass.material_models.find(omm => omm.modelCfg.id === model.modelCfg.id);
                    const existingLinkedInternalMedia = existingModel?.modelCfg.linked_media.find(olm => olm.type === 'internal' && linkedMedia.type === 'internal' && olm.file.id === linkedMedia.file.id);
                    // If the media is internal and the meta data has changed, update it
                    if (!existingLinkedInternalMedia || (existingLinkedInternalMedia.type === 'internal' && !isEqual(existingLinkedInternalMedia.file.meta, linkedMedia.file.meta))) {
                        await gqlClient.request({
                            document: UpdateArgoMediaDocument,
                            variables: {
                                mediaId: linkedMedia.file.id,
                                mediaInput: {
                                    meta: linkedMedia.file.meta,
                                },
                            },
                        });
                    }
                }
            }
        }
        return {
            status: 'success',
        };
    } catch (err) {
        return {
            status: 'error',
            errorMessage: `Error saving Material Config: "${JSON.stringify(err)}"`,
        };
    }
}
